import { createPortal } from 'react-dom'
import { useState, useEffect } from 'react'
import { X, Shield, Download, Trash2, FileText, CheckCircle } from 'lucide-react'
import { supabase } from '../../lib/supabase'

interface RGPDPanelProps {
  isOpen: boolean
  onClose: () => void
  user: any
}

export default function RGPDPanel({ isOpen, onClose, user }: RGPDPanelProps) {
  const [activeTab, setActiveTab] = useState<'privacy' | 'data' | 'consent'>('privacy')
  const [consents, setConsents] = useState({
    emailMarketing: false,
    dataAnalytics: true,
    thirdParty: false,
  })
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  console.log('🟣 RGPDPanel rendered, isOpen:', isOpen, 'mounted:', mounted)

  const downloadMyData = async () => {
    setLoading(true)
    setMessage(null)
    try {
      if (!user) {
        setMessage({ type: 'error', text: 'Debes iniciar sesion para descargar tus datos' })
        return
      }

      // Obtener datos del usuario desde Supabase
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single()

      const { data: courseProgress } = await supabase
        .from('course_progress')
        .select('*')
        .eq('user_id', user.id)

      const { data: signatures } = await supabase
        .from('campaign_signatures')
        .select('*')
        .eq('user_id', user.id)

      const userData = {
        profile,
        courseProgress,
        signatures,
        exportedAt: new Date().toISOString(),
      }

      // Crear blob y descargar
      const blob = new Blob([JSON.stringify(userData, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `mis-datos-${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      setMessage({ type: 'success', text: 'Datos descargados correctamente' })
    } catch (error) {
      console.error('Error downloading data:', error)
      setMessage({ type: 'error', text: 'Error al descargar los datos' })
    } finally {
      setLoading(false)
    }
  }

  const deleteMyAccount = async () => {
    if (!window.confirm('Estas seguro de que deseas eliminar tu cuenta? Esta accion no se puede deshacer.')) {
      return
    }

    setLoading(true)
    setMessage(null)
    try {
      if (!user) {
        setMessage({ type: 'error', text: 'Debes iniciar sesion' })
        return
      }

      // Eliminar datos del usuario
      await supabase.from('profiles').delete().eq('id', user.id)
      await supabase.from('course_progress').delete().eq('user_id', user.id)
      await supabase.from('campaign_signatures').delete().eq('user_id', user.id)
      await supabase.from('forum_posts').delete().eq('user_id', user.id)
      await supabase.from('user_badges').delete().eq('user_id', user.id)

      // Cerrar sesión
      await supabase.auth.signOut()
      
      setMessage({ type: 'success', text: 'Cuenta eliminada. Seras redirigido...' })
      setTimeout(() => {
        window.location.href = '/'
      }, 2000)
    } catch (error) {
      console.error('Error deleting account:', error)
      setMessage({ type: 'error', text: 'Error al eliminar la cuenta' })
    } finally {
      setLoading(false)
    }
  }

  const saveConsents = () => {
    localStorage.setItem('userConsents', JSON.stringify({
      ...consents,
      updatedAt: new Date().toISOString()
    }))
    setMessage({ type: 'success', text: 'Preferencias guardadas correctamente' })
  }

  if (!isOpen || !mounted) {
    console.log('🟣 Panel not rendering - isOpen:', isOpen, 'mounted:', mounted)
    return null
  }

  console.log('🟣 Panel IS OPEN - rendering panel content')

  const panelContent = (
    <>
      <div className="fixed inset-0 bg-black/50 z-[9998]" onClick={onClose}></div>
      <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl z-[9999] overflow-y-auto animate-slide-in-right">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-education/10 rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-education" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Centro de Privacidad</h2>
              <p className="text-sm text-gray-600">Gestiona tus datos y preferencias RGPD</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {message && (
          <div className={`mx-6 mt-4 p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            <div className="flex items-center gap-2">
              {message.type === 'success' ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <X className="w-5 h-5" />
              )}
              <span className="text-sm font-medium">{message.text}</span>
            </div>
          </div>
        )}

        <div className="p-6">
          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('privacy')}
              className={`px-4 py-3 font-semibold transition-colors ${
                activeTab === 'privacy'
                  ? 'text-education border-b-2 border-education'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Privacidad
            </button>
            <button
              onClick={() => setActiveTab('data')}
              className={`px-4 py-3 font-semibold transition-colors ${
                activeTab === 'data'
                  ? 'text-education border-b-2 border-education'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Mis Datos
            </button>
            <button
              onClick={() => setActiveTab('consent')}
              className={`px-4 py-3 font-semibold transition-colors ${
                activeTab === 'consent'
                  ? 'text-education border-b-2 border-education'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Consentimientos
            </button>
          </div>

          {/* Privacidad Tab */}
          {activeTab === 'privacy' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Tus derechos RGPD</h3>
                <p className="text-gray-700 mb-4">
                  Como usuario de Learn to Lead Change, tienes los siguientes derechos sobre tus datos personales:
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-action flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold text-gray-900">Derecho de acceso</div>
                      <div className="text-sm text-gray-600">Puedes solicitar una copia de todos tus datos</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-action flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold text-gray-900">Derecho de rectificacion</div>
                      <div className="text-sm text-gray-600">Puedes corregir datos incorrectos en tu perfil</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-action flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold text-gray-900">Derecho de supresion</div>
                      <div className="text-sm text-gray-600">Puedes eliminar tu cuenta y todos tus datos</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-action flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold text-gray-900">Derecho de portabilidad</div>
                      <div className="text-sm text-gray-600">Puedes descargar tus datos en formato JSON</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-education/5 rounded-lg border border-education/20">
                <h4 className="font-semibold text-gray-900 mb-2">Necesitas ayuda?</h4>
                <p className="text-sm text-gray-700">
                  Para ejercer cualquiera de estos derechos o si tienes preguntas sobre como manejamos tus datos,
                  contactanos en: <a href="mailto:privacidad@learntoleadchange.org" className="text-education font-semibold hover:underline">
                    privacidad@learntoleadchange.org
                  </a>
                </p>
              </div>
            </div>
          )}

          {/* Mis Datos Tab */}
          {activeTab === 'data' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Gestion de datos personales</h3>
                <p className="text-gray-700 mb-6">
                  Descarga o elimina todos tus datos de forma permanente.
                </p>

                <div className="space-y-4">
                  <div className="p-6 bg-gray-50 rounded-xl border border-gray-200">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-education/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Download className="w-6 h-6 text-education" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 mb-2">Descargar mis datos</h4>
                        <p className="text-sm text-gray-600 mb-4">
                          Obten un archivo JSON con toda tu informacion: perfil, progreso de cursos, 
                          firmas de campanas y participacion en foros.
                        </p>
                        <button
                          onClick={downloadMyData}
                          disabled={loading || !user}
                          className="px-6 py-2.5 bg-education text-white rounded-lg font-semibold hover:bg-education/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {loading ? 'Descargando...' : 'Descargar datos'}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 bg-red-50 rounded-xl border border-red-200">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Trash2 className="w-6 h-6 text-red-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 mb-2">Eliminar mi cuenta</h4>
                        <p className="text-sm text-gray-600 mb-4">
                          Esta accion es permanente y no se puede deshacer. Se eliminaran todos tus datos, 
                          progreso, badges y participaciones.
                        </p>
                        <button
                          onClick={deleteMyAccount}
                          disabled={loading || !user}
                          className="px-6 py-2.5 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {loading ? 'Eliminando...' : 'Eliminar cuenta'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Consentimientos Tab */}
          {activeTab === 'consent' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Gestiona tus consentimientos</h3>
                <p className="text-gray-700 mb-6">
                  Controla como usamos tu informacion y que comunicaciones recibes.
                </p>

                <div className="space-y-4">
                  <div className="p-5 bg-gray-50 rounded-xl">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 mb-2">Cookies necesarias</h4>
                        <p className="text-sm text-gray-600">
                          Esenciales para el funcionamiento de la plataforma (sesion, seguridad, preferencias).
                        </p>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={true}
                          disabled
                          className="w-5 h-5 text-education rounded focus:ring-education"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="p-5 bg-white rounded-xl border border-gray-200">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 mb-2">Marketing por email</h4>
                        <p className="text-sm text-gray-600">
                          Recibe novedades, nuevos cursos y campanas destacadas en tu correo.
                        </p>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={consents.emailMarketing}
                          onChange={(e) => setConsents({ ...consents, emailMarketing: e.target.checked })}
                          className="w-5 h-5 text-education rounded focus:ring-education cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="p-5 bg-white rounded-xl border border-gray-200">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 mb-2">Analitica de datos</h4>
                        <p className="text-sm text-gray-600">
                          Ayudanos a mejorar la plataforma permitiendo el analisis de uso anonimizado.
                        </p>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={consents.dataAnalytics}
                          onChange={(e) => setConsents({ ...consents, dataAnalytics: e.target.checked })}
                          className="w-5 h-5 text-education rounded focus:ring-education cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="p-5 bg-white rounded-xl border border-gray-200">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 mb-2">Compartir con terceros</h4>
                        <p className="text-sm text-gray-600">
                          Permitir compartir datos anonimizados con ONG's aliadas para investigacion de impacto social.
                        </p>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={consents.thirdParty}
                          onChange={(e) => setConsents({ ...consents, thirdParty: e.target.checked })}
                          className="w-5 h-5 text-education rounded focus:ring-education cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={saveConsents}
                    className="w-full px-6 py-3 bg-education text-white rounded-lg font-semibold hover:bg-education/90 transition-colors"
                  >
                    Guardar preferencias
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Links a documentos legales */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Documentos legales</h4>
            <div className="space-y-2">
              <a href="/privacidad" className="flex items-center gap-2 text-sm text-education hover:underline">
                <FileText className="w-4 h-4" />
                Politica de Privacidad
              </a>
              <a href="/cookies" className="flex items-center gap-2 text-sm text-education hover:underline">
                <FileText className="w-4 h-4" />
                Politica de Cookies
              </a>
              <a href="/terminos" className="flex items-center gap-2 text-sm text-education hover:underline">
                <FileText className="w-4 h-4" />
                Terminos y Condiciones
              </a>
              <a href="/legal" className="flex items-center gap-2 text-sm text-education hover:underline">
                <FileText className="w-4 h-4" />
                Aviso Legal
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  )

  // Renderizar usando portal directamente en body
  return createPortal(panelContent, document.body)
}
